'use client';

import { SubscriptionLevelBadge } from '@ps/features/subscription/components/SubscriptionLevelBadge';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/features/subscription/types/SubscriptionLevel';
import { X } from 'lucide-react';

import { ChannelAvatar } from '../common/ChannelAvatar';

interface Props {
  displayName: string;
  avatar?: string;
  /** The message author's subscription tier — shows the Fan/Subscriber/VIP badge. */
  subscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  /** The author is the viewer themselves — labels "(You)", suppresses actions. */
  isSelf: boolean;
  /**
   * The viewer may moderate this author (owner viewing a NON-owner member) —
   * gates the Send Message + Block actions.
   */
  canModerate: boolean;
  /** Current blocked state — labels the moderation button Block vs Unblock. */
  blocked: boolean;
  onSendMessage: () => void;
  onBlock: () => void;
  onClose: () => void;
}

const kActionButton =
  'flex-1 rounded-full border border-line-strong px-4 py-2.5 text-sm font-semibold text-content-1 transition-colors hover:bg-surface-2';

/**
 * The floating user card the channel owner opens from a message author (avatar /
 * name) — a 1:1 port of PopshopRN's `CommunityChannelMessageUserCard`: the
 * author's avatar + name + subscription-tier badge, and — for a non-owner member
 * — "Send Message" (opens the transient DM window) + "Block User". Rendered inside
 * a popover anchored on the author; the host owns positioning + open state.
 */
export function CommunityChannelMessageUserCard({
  displayName,
  avatar,
  subscriptionLevel,
  isSelf,
  canModerate,
  blocked,
  onSendMessage,
  onBlock,
  onClose,
}: Props) {
  return (
    <div className="text-content-1 border-line-strong bg-surface-1 relative w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border shadow-2xl">
      <div className="flex items-center gap-4 p-4 pr-9">
        <ChannelAvatar
          src={avatar}
          name={displayName}
          size={64}
          className="rounded-lg"
        />
        <div className="flex min-w-0 flex-col items-start gap-2">
          <span className="text-base font-bold">
            {displayName}
            {isSelf ? (
              <span className="text-content-4 font-normal"> (You)</span>
            ) : null}
          </span>
          {subscriptionLevel != null && !isSelf ? (
            <SubscriptionLevelBadge level={subscriptionLevel} />
          ) : null}
        </div>
      </div>

      {canModerate ? (
        <div className="border-line-strong/70 flex items-center gap-3 border-t p-4">
          <button
            type="button"
            onClick={onSendMessage}
            className={kActionButton}
          >
            Send Message
          </button>
          <button type="button" onClick={onBlock} className={kActionButton}>
            {blocked ? 'Unblock User' : 'Block User'}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="hover:text-content-1 text-content-3 hover:bg-surface-2 absolute right-2.5 top-2.5 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
